import { WmButton } from "@watermarkinsights/ripple-react";
import { useContext, useEffect, useReducer } from "react";
import { sendRequest } from "@/common/http";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import { ReportingYear } from "@/features/common/interfaces/Interface";
import translate from "@/i18n/translate";
import "../../sll/Sll.scss";
import { ParticipationOverview } from "./ParticipationOverview";
import { LearningAssessments } from "@/features/sll/Sll";
import { useNavigate } from "react-router-dom";
import { scrollToTop } from "@/features/common/helper";

const stateReducer = (state: ProgramInsightsState, action: Action) => {
  switch (action.type) {
    case "showLoader":
      return { ...state, loader: true };
    case "setLearningAssessments":
      return {
        ...state,
        learningAssessments: action.learningAssessments,
        loader: false
      };
    case "setCurrentReportingYear":
      return {
        ...state,
        reportingYear: action.reportingYear
      };
    default:
      return state;
  }
};

interface ProgramInsightsState {
  loader: boolean;
  learningAssessments: LearningAssessments;
  reportingYear: ReportingYear | null;
}

interface Action {
  type: string;
  learningAssessments: LearningAssessments;
  reportingYear: ReportingYear | null;
}

const defaultAction: Action = {
  type: "",
  learningAssessments: {
    noActivityTerms: "",
    participationOverview: null,
    studentAssessmentProgressByTerm: []
  },
  reportingYear: null
};

interface ProgramInsightsProps {
  currentReportingYear: ReportingYear | null;
}

export function ProgramInsights({
  currentReportingYear
}: ProgramInsightsProps) {
  const { institution, sysUser } = useContext<AppContextInterface>(AppContext);
  const initialState: ProgramInsightsState = {
    loader: true,
    learningAssessments: {
      noActivityTerms: "",
      participationOverview: null,
      studentAssessmentProgressByTerm: []
    },
    reportingYear: null
  };

  const [state, dispatch] = useReducer(stateReducer, initialState);
  const navigate = useNavigate();

  useEffect(() => {
    if (sysUser && sysUser.roles.includes("administrator")) {
      dispatch({
        ...defaultAction,
        type: "setCurrentReportingYear",
        reportingYear: currentReportingYear
      });
    }
  }, [currentReportingYear]);

  useEffect(() => {
    if (institution && institution.platformUuid && state.reportingYear) {
      const applyResult = (result: LearningAssessments) => {
        dispatch({
          ...defaultAction,
          type: "setLearningAssessments",
          learningAssessments: result
        });
      };

      dispatch({ ...defaultAction, type: "showLoader" });

      sendRequest(
        {
          uri: `/sll/learning_assessments?start_date=${
          state.reportingYear.startDate
          }&end_date=${state.reportingYear.endDate}&with_activities=${false}`,
          camelize: true
        },
        applyResult
      );
    }
  }, [institution && institution.platformUuid, state.reportingYear]);

  function showParticipatioOverview(
    reportingYear: ReportingYear | null,
    learningAssessments: LearningAssessments
  ) {
    return (
      reportingYear &&
      learningAssessments.participationOverview !== null &&
      learningAssessments.studentAssessmentProgressByTerm?.length > 0
    );
  }

  function noLearningAssessments(
    reportingYear: ReportingYear | null,
    learningAssessments: LearningAssessments
  ) {
    return (
      (!reportingYear && reportingYear !== null) ||
      (!state.loader &&
        learningAssessments.studentAssessmentProgressByTerm?.length === 0)
    );
  }

  const queryString = window.location.search;
  let academicAndOfficesUrl = queryString.includes("locale")
    ? `/insights/academic_office/${queryString}`
    : "/insights/academic_office";

  function navigateToAcademicAndOffices(url: string) {
    scrollToTop(".home-page-header");
    navigate(url);
  }

  return (
    <div className="institution-insights-card paper-card">
      <div className="header-container header">
        <div>
          <div className="institution-name flex-row">
            <div>
              <WmButton
                buttonType="textonly"
                onClick={() =>
                  navigateToAcademicAndOffices(academicAndOfficesUrl)
                }
              >
                <span className="program-link">
                  {translate("app.home.programInsightsHeading")}
                </span>
              </WmButton>
            </div>
          </div>
          <div>{translate("app.home.programInsightsDesc")}</div>
        </div>
      </div>
      <div className="body">
        <div className="participation-overview-container">
          {state.loader && state.reportingYear && (
            <WidgetLoader
              loaderContainerClasses="loader"
              loaderClasses="content"
            />
          )}
          {showParticipatioOverview(
            state.reportingYear,
            state.learningAssessments
          ) && (
              <ParticipationOverview
                participationOverview={
                  state.learningAssessments.participationOverview
                }
                reportingYear={state.reportingYear}
              />
            )}
          {noLearningAssessments(
            state.reportingYear,
            state.learningAssessments
          ) && <NoLearningAssessments />}
        </div>
      </div>
    </div>
  );
}

const NoLearningAssessments = () => {
  return (
    <div className="no-assesments">
      <div className="no-assesments-text">
        {translate("app.home.noSlldata")}
      </div>
    </div>
  );
};
